import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { getDB, isFirebaseConfigured } from '../../lib/firebase';
import { 
  doc, 
  getDoc, 
  updateDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { COLLECTIONS, Bike, ServiceCentre } from '../../lib/database.types';

interface EditBikeModalProps {
  bikeId: string;
  onClose: () => void;
  onBikeUpdated: () => void;
}

export function EditBikeModal({ bikeId, onClose, onBikeUpdated }: EditBikeModalProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    make: '',
    model: '',
    rego_number: '',
    rego_expiry: '',
    rate_a: 0,
    rate_b: 0,
    daily_rate: 0,
    service_centre: 'five_dock' as ServiceCentre,
    notes: '',
    status: 'available'
  });

  useEffect(() => {
    fetchBike();
  }, [bikeId]);

  const fetchBike = async () => {
    try {
      setLoading(true);
      
      if (!isFirebaseConfigured()) {
        // Mock data for development
        setFormData({
          make: 'Honda',
          model: 'CBR300R',
          rego_number: 'ABC123',
          rego_expiry: '2024-12-31',
          rate_a: 45,
          rate_b: 35,
          daily_rate: 40,
          service_centre: 'five_dock',
          notes: 'Mock bike data',
          status: 'available'
        });
        return;
      }

      const bikeDoc = await getDoc(doc(getDB(), COLLECTIONS.BIKES, bikeId));
      if (bikeDoc.exists()) {
        const bikeData = bikeDoc.data() as Bike;
        setFormData({
          make: bikeData.make || '',
          model: bikeData.model || '',
          rego_number: bikeData.rego_number || '',
          rego_expiry: bikeData.rego_expiry || '',
          rate_a: bikeData.rate_a || 0,
          rate_b: bikeData.rate_b || 0,
          daily_rate: bikeData.daily_rate || 0,
          service_centre: (bikeData.service_centre as ServiceCentre) || 'five_dock',
          notes: bikeData.notes || '',
          status: bikeData.status || 'available'
        });
      }
    } catch (error) {
      console.error('Error fetching bike:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      if (!isFirebaseConfigured()) {
        console.log('Mock: Would update bike:', formData);
        onBikeUpdated();
        onClose();
        return;
      }

      await updateDoc(doc(getDB(), COLLECTIONS.BIKES, bikeId), {
        ...formData,
        updated_at: serverTimestamp()
      });

      onBikeUpdated();
      onClose();
    } catch (error) {
      console.error('Error updating bike:', error);
      alert('Error updating bike');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name.includes('rate') ? Number(value) : value
    }));
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Edit Bike</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Make
              </label>
              <input
                type="text"
                name="make"
                value={formData.make}
                onChange={handleChange}
                required
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Model
              </label>
              <input
                type="text"
                name="model"
                value={formData.model}
                onChange={handleChange}
                required
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Registration Number
              </label>
              <input
                type="text"
                name="rego_number"
                value={formData.rego_number}
                onChange={handleChange}
                required
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Registration Expiry
              </label>
              <input
                type="date"
                name="rego_expiry"
                value={formData.rego_expiry}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rate A ($)
              </label>
              <input
                type="number"
                name="rate_a"
                value={formData.rate_a}
                onChange={handleChange}
                min="0"
                step="0.01"
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rate B ($)
              </label>
              <input
                type="number"
                name="rate_b"
                value={formData.rate_b}
                onChange={handleChange}
                min="0"
                step="0.01"
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Daily Rate ($)
              </label>
              <input
                type="number"
                name="daily_rate"
                value={formData.daily_rate}
                onChange={handleChange}
                min="0"
                step="0.01"
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="available">Available</option>
                <option value="delivered">Delivered</option>
                <option value="maintenance">Maintenance</option>
                <option value="decommissioned">Decommissioned</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Service Centre
              </label>
              <select
                name="service_centre"
                value={formData.service_centre}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md"
              >
                <option value="five_dock">Five Dock</option>
                <option value="seven_hills">Seven Hills</option>
                <option value="reza">Reza</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}